import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'
import { DeviceType } from '@/src/types/deviceType'
import { PersonOutlined } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Typography
} from '@mui/material'

export interface TravellerDisplayItem {
    label: string
    name: string
    dob?: string
    docNumber?: string
    docExpiry?: string
}

export interface ContactDisplay {
    phone: string
    email: string
}

export interface TravellerReviewOverlayProps {
    open: boolean
    onClose: () => void
    onConfirm: () => void
    isLoading: boolean
    deviceType: DeviceType
    contact: ContactDisplay
    travellerItems: TravellerDisplayItem[]
    titleLabel: string
    contactSectionLabel: string
    phoneLabel: string
    emailLabel: string
    dobLabel: string
    passportNumberLabel: string
    expiryDateLabel: string
    continueToPaymentLabel: string
}

const cardSx = {
    bgcolor: 'background.paper',
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'grey.200',
    p: 2,
    mb: 1.5,
    width: '100%',
    minWidth: 320,
    boxSizing: 'border-box'
}

const DetailRow = ({ label, value }: { label: string; value: string }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, py: 0.75 }}>
        <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontSize: 14, flexShrink: 0, maxWidth: '42%' }}
        >
            {label}
        </Typography>
        <Typography
            variant="body2"
            sx={{
                fontWeight: 600,
                fontSize: 14,
                color: 'text.primary',
                textAlign: 'right',
                flex: 1,
                minWidth: 0,
                overflowWrap: 'anywhere'
            }}
        >
            {value}
        </Typography>
    </Box>
)

const ReviewContent = ({
    contact,
    travellerItems,
    contactSectionLabel,
    phoneLabel,
    emailLabel,
    dobLabel,
    passportNumberLabel,
    expiryDateLabel,
    onConfirm,
    isLoading,
    continueToPaymentLabel
}: Omit<TravellerReviewOverlayProps, 'open' | 'onClose' | 'deviceType' | 'titleLabel'>) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, bgcolor: 'neutral.100' }}>
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto', px: 2, py: 2, minWidth: 0 }}>
            <Box sx={cardSx}>
                <Typography
                    variant="body1"
                    sx={{ fontWeight: 700, fontSize: 15, color: 'text.primary', mb: 1.5 }}
                >
                    {contactSectionLabel}
                </Typography>
                {contact.phone && <DetailRow label={phoneLabel} value={contact.phone} />}
                {contact.email && <DetailRow label={emailLabel} value={contact.email} />}
            </Box>

            {travellerItems.map((item, idx) => (
                <Box key={idx} sx={cardSx}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 2,
                            mb: 1.5
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 700,
                                fontSize: 15,
                                color: 'text.primary',
                                flex: 1,
                                minWidth: 0,
                                pr: 1
                            }}
                        >
                            {item.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 'auto' }}>
                            <PersonOutlined sx={{ fontSize: 18, color: 'grey.500' }} />
                            <Typography variant="body2" sx={{ fontSize: 14, color: 'text.secondary', fontWeight: 500 }}>
                                {item.label}
                            </Typography>
                        </Box>
                    </Box>
                    {item.dob && <DetailRow label={dobLabel} value={item.dob} />}
                    {item.docNumber && <DetailRow label={passportNumberLabel} value={item.docNumber} />}
                    {item.docExpiry && <DetailRow label={expiryDateLabel} value={item.docExpiry} />}
                </Box>
            ))}
        </Box>

        <Box
            sx={{
                p: 2,
                bgcolor: 'background.paper',
                borderTop: '1px solid',
                borderColor: 'grey.200'
            }}
        >
            <Button
                fullWidth
                variant="customVariantPrimary"
                onClick={onConfirm}
                disabled={isLoading}
                loading={isLoading}
                sx={{
                    bgcolor: 'brand.primary',
                    color: 'brand.primaryForeground',
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    '&:hover': { bgcolor: 'brand.primaryHover' }
                }}
            >
                {continueToPaymentLabel}
            </Button>
        </Box>
    </Box>
)

const TravellerReviewOverlay = (props: TravellerReviewOverlayProps) => {
    const {
        open,
        onClose,
        deviceType,
        titleLabel,
        ...contentProps
    } = props

    if (deviceType === 'DESKTOP') {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            width: '100%',
                            minWidth: { xs: 320, sm: 420 },
                            maxWidth: 480,
                            maxHeight: {
                                xs: '85dvh',
                                md: 620
                            },
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        }
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 1.5,
                        pt: 2.5,
                        px: 2.5,
                        bgcolor: 'background.paper'
                    }}
                >
                    <Typography
                        component="span"
                        variant="h6"
                        sx={{ fontWeight: 700, fontSize: '1.125rem', color: 'text.primary' }}
                    >
                        {titleLabel}
                    </Typography>
                    <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent
                    sx={{
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        bgcolor: 'neutral.100'
                    }}
                >
                    <ReviewContent {...contentProps} />
                </DialogContent>
            </Dialog>
        )
    }

    return (
        <SwipeableDrawerWithBackNavigation open={open} onClose={onClose} disableSwipeToOpen>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    pt: 1.5,
                    pb: 1.5,
                    bgcolor: 'background.paper'
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: 'text.primary' }}>
                    {titleLabel}
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <Divider />
            <ReviewContent {...contentProps} />
        </SwipeableDrawerWithBackNavigation>
    )
}

export { TravellerReviewOverlay }
